import clsx from "clsx";
import format from "date-fns/format";
import get from "lodash/get";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import DealerChart from "~/components/Chart";
import PanelWrapper from "~/components/PanelWrapper";
import Button from "~/shared/ui/Fields/Button";
import useVersionContextSpecificSet from "~/versionControl/hooks/useVersionContextSpecificSet";
import { getIndicatorName, getIndicatorValue } from "~/libs/getData";
import { getIndicatorPeriod } from "~/libs/getFormatDate";
import {
  useGetSalesUsedCarQuery,
  useGetWorkSpaceNewVehicleQuery,
} from "~/schemas/generated/graphql";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";

import s from "./BlockNewVehicle.less";

const BlockNewVehicle = () => {
  const { t } = useTranslation();
  const currentDealerId = useCurrentDealerId();
  const [showNew, setShowNew] = useState(true);

  const { data, loading } = useGetWorkSpaceNewVehicleQuery({
    variables: {
      DealerId: currentDealerId,
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: usedData, loading: loadingUsed } = useGetSalesUsedCarQuery({
    variables: { dealerId: currentDealerId },
    fetchPolicy: "cache-and-network",
  });

  /**
   * Показатели 105 - 106 убраны по заявке 46338
   * https://seotlt.bitrix24.ru/workgroups/group/330/tasks/task/view/46338/
   */

  const {
    i101 = [],
    i103 = [],
    i104 = [],
  } = get(data, "WorkSpaceGetter.getNewVehicle", {});

  const usedSalesData = get(usedData, "WorkSpaceGetter.getSalesUsedCar", {});

  const dealerNewVehiclesForList = [...i103.slice(-1), ...i104.slice(-1)].map(
    ({ doubleValue, Indicator, createdAt }) => ({
      name: getIndicatorName(Indicator),
      month: getIndicatorPeriod({ createdAt }, "specMonth"),
      value: getIndicatorValue(Indicator, 2, null, doubleValue),
    }),
  );

  const dealerUsedVehiclesForList =
    Array.isArray(usedSalesData) &&
    usedSalesData.slice(-1).map(({ avgSales, Indicator, updatedAt }) => ({
      name: "Средние продажи",
      month: getIndicatorPeriod({ createdAt: updatedAt }, "specMonth"),
      value: getIndicatorValue(Indicator, 2, null, avgSales),
    }));

  const newVehiclesForChartLabels = i101.map(({ createdAt }, i, arr) => {
    const year = getIndicatorPeriod({ createdAt }, "specYear");
    const month = getIndicatorPeriod({ createdAt }, "specMonth");
    return i === arr.length - 1 ? [year, month] : year;
  });

  const usedVehiclesForChartLabels =
    usedData?.WorkSpaceGetter?.getSalesUsedCar?.map(
      ({ updatedAt, createdAt }, i, arr) => {
        const year = getIndicatorPeriod({ createdAt }, "specYear");
        const month = getIndicatorPeriod({ createdAt: updatedAt }, "specMonth");
        return i === arr.length - 1 ? [year, month] : year;
      },
    );

  const newVehiclesForChartValues = i101.map(
    ({ doubleValue }) => +doubleValue.toFixed(0),
  );
  const usedVehiclesForChartValues =
    usedData?.WorkSpaceGetter?.getSalesUsedCar?.map(
      ({ doubleValue }) => +doubleValue.toFixed(0),
    );

  const newVehicleValue = newVehiclesForChartValues.slice(-1)[0];
  const newVehicleDate = i101.slice(-1)[0]?.createdAt;
  const formattedNewVehicleDate = newVehicleDate
    ? format(new Date(newVehicleDate), "yy/MM")
    : "";

  const usedVehicleValue = usedVehiclesForChartValues?.slice(-1)[0];
  const usedVehicleDate =
    usedData?.WorkSpaceGetter?.getSalesUsedCar?.slice(-1)[0]?.updatedAt;
  const formattedUsedVehicleDate = usedVehicleDate
    ? format(new Date(usedVehicleDate), "yy/MM")
    : "";

  // VERSION_CONTROL_ASP
  const hideASPButton = get(
    useVersionContextSpecificSet(),
    "page.main.hideASPButton",
    false,
  );

  return (
    <PanelWrapper
      width={330}
      height={220}
      label={t(`dealers.${showNew ? "new" : "used"}_vehicle`)}
      customClassName={s.new_car_padding}
      loading={loading || loadingUsed}
    >
      <div className={s.vehicle}>
        <div className={clsx([s.vehicle_block])}>
          <div className={s.text}>
            <ul className={s.list}>
              {(showNew
                ? dealerNewVehiclesForList
                : dealerUsedVehiclesForList
              ).map(({ name, month, value }) => (
                <li key={name}>
                  {`${name} ${month}: `}
                  <strong>{value}</strong>
                </li>
              ))}
            </ul>

            {/*VERSION_CONTROL_ASP*/}

            {!!newVehiclesForChartValues.length && !hideASPButton && (
              <Button
                small
                customClassName={s.navButton}
                customIconClassName={s.navButtonIcon}
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? (
                  <div className={s.button_content}>
                    <div className={s.button_row}>
                      <div className={s.white_text}>АСП</div>
                      <div className={s.black_text}>{usedVehicleValue}</div>
                    </div>
                    <span>({formattedUsedVehicleDate})</span>
                  </div>
                ) : (
                  <div className={s.button_content}>
                    <div className={s.button_row}>
                      <div className={s.white_text}>НА</div>
                      <div className={s.black_text}>{newVehicleValue}</div>
                    </div>
                    <span>({formattedNewVehicleDate})</span>
                  </div>
                )}
              </Button>
            )}
          </div>
          <div className={s.vehicle_img}>
            <DealerChart
              values={
                showNew ? newVehiclesForChartValues : usedVehiclesForChartValues
              }
              labels={
                showNew ? newVehiclesForChartLabels : usedVehiclesForChartLabels
              }
              type={showNew ? "new_vehicle" : "used_vehicle"}
              customClassName={s.vehicle_chart_default}
            />
          </div>
        </div>
      </div>
    </PanelWrapper>
  );
};

export default BlockNewVehicle;
